import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Plus, Edit, Trash2, Eye, FileUp, FileMinus } from 'lucide-react';
import { toast } from 'react-toastify';
import { LysepAPI } from '../../../contexts/ApiContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/Table";
import { Button, IconButton } from "../../../components/ui/Button";
import { Badge } from '../../../components/ui/Badge';
import { Pagination } from "../../../components/ui/Pagination";

const PostList = forwardRef(({ canManage, onEdit, onDelete, onPublish, onUnpublish }, ref) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * limit;
      const response = await LysepAPI.get('/api/blog/admin/posts', {
        params: { limit, offset }
      });

      if (response.data && response.data.state && response.data.data) {
        const fetchedPosts = response.data.data.posts || [];
        const totalCount = response.data.data.count || fetchedPosts.length;

        setPosts(fetchedPosts);
        setTotalPosts(totalCount);
        setTotalPages(Math.ceil(totalCount / limit));
      } else {
        toast.error("Postlar yüklenemedi.");
      }
    } catch (error) {
      console.error('Blog API Error:', error);
      toast.error("Veri çekme hatası.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page]);

  useImperativeHandle(ref, () => ({
    refresh: fetchData
  }));

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleDelete = (id) => {
    if (window.confirm('Bu postu silmek istediğinize emin misiniz?')) {
      onDelete(id);
    }
  };

  return (
    <div className="space-y-4">
      {canManage && (
        <div className="flex justify-end">
          <Button
            onClick={() => onEdit(null)}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Yeni Post
          </Button>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[60px]">ID</TableHead>
            <TableHead>Başlık</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead>Durum</TableHead>
            <TableHead>Öne Çıkan</TableHead>
            <TableHead>Görüntülenme</TableHead>
            <TableHead>Yayın Tarihi</TableHead>
            <TableHead className="text-right">İşlemler</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} className="h-24 text-center">
                Yükleniyor...
              </TableCell>
            </TableRow>
          ) : posts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="h-24 text-center text-gray-400">
                Post bulunamadı.
              </TableCell>
            </TableRow>
          ) : (
            posts.map((post) => (
              <TableRow key={post.id}>
                <TableCell>{post.id}</TableCell>
                <TableCell className="font-medium">{post.title}</TableCell>
                <TableCell className="text-gray-400">{post.slug}</TableCell>
                <TableCell>
                  <Badge variant={
                    {
                      'draft': 'secondary',
                      'published': 'success',
                      'archived': 'warning'
                    }[post.status || 'draft'] || 'secondary'
                  }>
                    {(post.status || 'draft').toUpperCase()}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={post.is_featured ? 'default' : 'secondary'}>
                    {post.is_featured ? 'EVET' : 'HAYIR'}
                  </Badge>
                </TableCell>
                <TableCell>{post.view_count}</TableCell>
                <TableCell>
                  {post.published_at ? new Date(post.published_at).toLocaleDateString('tr-TR') : '-'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {!canManage && (
                      <IconButton
                        onClick={() => onEdit(post)}
                        variant="ghost"
                        size="iconSm"
                        icon={Eye}
                      />
                    )}
                    {canManage && (
                      <>
                        <IconButton
                          onClick={() => onEdit(post)}
                          variant="warning"
                          size="iconSm"
                          icon={Edit}
                        />
                        {post.status === 'published' ? (
                          <IconButton
                            onClick={() => onUnpublish(post.id)}
                            variant="ghost"
                            size="iconSm"
                            icon={FileMinus}
                            title="Yayından Kaldır"
                          />
                        ) : (
                          <IconButton
                            onClick={() => onPublish(post.id)}
                            variant="success"
                            size="iconSm"
                            icon={FileUp}
                            title="Yayınla"
                          />
                        )}
                        <IconButton
                          onClick={() => handleDelete(post.id)}
                          variant="destructive"
                          size="iconSm"
                          icon={Trash2}
                        />
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
});

PostList.displayName = 'PostList';

export default PostList;

